import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { getBackendUrl } from "@/lib/backend";
import {
  ragLabel,
  ragPillClass,
  formatLakh,
  healthNarrative,
  buildBlockMetrics,
  buildInsightCards,
  buildSignalCards,
  buildPerformanceMatrix,
  buildActionQueue,
  schoolStatusMeta,
  filterSchools,
  exportSchoolsCsv,
} from "@/utils/blockDetailDerived";
import BlockKpiSchoolsModal from "@/components/BlockKpiSchoolsModal";
import "./BlockDetail.css";

const API = `${getBackendUrl()}/api`;
const PAGE_SIZE = 10;

const TABS = [
  { id: "overview", label: "Overview", icon: "chart" },
  { id: "insights", label: "AI Insights", icon: "ai" },
  { id: "diagnostics", label: "Diagnostics", icon: "book" },
  { id: "schools", label: "Schools", icon: "school" },
  { id: "actions", label: "Action Queue", icon: "action" },
];

const TabIcon = ({ type }) => {
  if (type === "chart") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 3v18h18" />
        <path d="M7 16v-5" />
        <path d="M12 16V7" />
        <path d="M17 16v-3" />
      </svg>
    );
  }
  if (type === "ai") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2v20" />
        <path d="M2 12h20" />
        <path d="M5 5l14 14" />
        <path d="M19 5 5 19" />
      </svg>
    );
  }
  if (type === "book") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5z" />
      </svg>
    );
  }
  if (type === "school") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 21h18" />
        <path d="M5 21V7l8-4v18" />
        <path d="M19 21V11l-6-4" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 11l2 2 4-4" />
      <path d="M5 4h14v16H5z" />
      <path d="M9 2h6v4H9z" />
    </svg>
  );
};

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);

const scoreRingStyle = (shi, toneClass) => {
  const pct = Math.min(Math.max(shi, 0), 100);
  const color = toneClass === "good" ? "#10b981" : toneClass === "amber" ? "#f59e0b" : "#ef4444";
  return {
    background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(${color} 0 ${pct}%, rgba(16,185,129,.14) ${pct}% 100%)`,
  };
};

const BlockDetail = () => {
  const { blockCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [block, setBlock] = useState(null);
  const [schools, setSchools] = useState([]);
  const [infra, setInfra] = useState(null);
  const [operational, setOperational] = useState(null);
  const [teachers, setTeachers] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [kpiModalMetric, setKpiModalMetric] = useState(null);

  const scopeHeaders = { "x-skip-scope": "true" };

  const fetchData = async () => {
    setLoading(true);
    try {
      const blockRes = await axios.get(`${API}/blocks/${blockCode}`);
      const blockData = blockRes.data;
      setBlock(blockData);

      const blockNameKey = String(blockData.block_name || "").trim().toUpperCase();
      const blockScopeParams = {
        district_code: blockData.district_code,
        block_code: blockCode,
        block_name: blockNameKey,
        district_name: blockData.district_name,
      };

      const [schoolsRes, infraRes, opsRes, teacherRes] = await Promise.all([
        axios.get(`${API}/blocks/${blockCode}/schools`, {
          params: { limit: 1000, sort_by: "shi_score", sort_order: "asc" },
        }),
        axios.get(`${API}/classrooms-toilets/block-wise`, {
          params: blockScopeParams,
          headers: scopeHeaders,
        }),
        axios.get(`${API}/executive/operational-performance`, {
          params: { block_code: blockCode, district_code: blockData.district_code },
          headers: scopeHeaders,
        }),
        axios.get(`${API}/teacher/block-wise`, {
          params: { block_code: blockCode },
          headers: scopeHeaders,
        }),
      ]);

      setSchools(schoolsRes.data || []);

      const infraRows = Array.isArray(infraRes.data) ? infraRes.data : [];
      const infraMatch =
        infraRows.find((r) => String(r.block_name || "").trim().toUpperCase() === blockNameKey) || infraRows[0] || null;
      setInfra(infraMatch);

      const opsBlocks = opsRes.data?.block_performance || [];
      const opsMatch =
        opsBlocks.find(
          (b) => String(b.block_code || "") === String(blockCode) || String(b.block_name || "").trim().toUpperCase() === blockNameKey,
        ) || opsBlocks[0] || null;
      setOperational(opsMatch);

      const teacherRows = Array.isArray(teacherRes.data) ? teacherRes.data : [];
      const teacherMatch =
        teacherRows.find(
          (b) => String(b.block_code || "") === String(blockCode) || String(b.block_name || "").trim().toUpperCase() === blockNameKey,
        ) || teacherRows[0] || null;
      setTeachers(teacherMatch);
    } catch (error) {
      console.error("Error fetching block data:", error);
      setBlock(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    setActiveTab("overview");
    setSearchQuery("");
    setSchoolFilter("all");
    setCategoryFilter("all");
    setPage(1);
  }, [blockCode]);

  const metrics = useMemo(() => buildBlockMetrics(schools), [schools]);
  const narrative = useMemo(() => (block ? healthNarrative(block, metrics) : null), [block, metrics]);
  const insightCards = useMemo(
    () => (block ? buildInsightCards({ block, infra, operational, teachers, metrics }) : []),
    [block, infra, operational, teachers, metrics],
  );
  const signalCards = useMemo(() => buildSignalCards(metrics), [metrics]);
  const performanceRows = useMemo(
    () => (block ? buildPerformanceMatrix({ block, infra, operational }) : []),
    [block, infra, operational],
  );
  const actionQueue = useMemo(() => buildActionQueue(metrics), [metrics]);

  const categories = useMemo(() => {
    const set = new Set();
    schools.forEach((s) => {
      const cat = s.school_category || s.school_management;
      if (cat) set.add(cat);
    });
    return ["all", ...Array.from(set).sort()];
  }, [schools]);

  const filteredSchools = useMemo(
    () => filterSchools(schools, { query: searchQuery, filter: schoolFilter, category: categoryFilter }),
    [schools, searchQuery, schoolFilter, categoryFilter],
  );

  const totalPages = Math.max(1, Math.ceil(filteredSchools.length / PAGE_SIZE));
  const pageRows = filteredSchools.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, schoolFilter, categoryFilter]);

  const scrollToSection = (tabId) => {
    setActiveTab(tabId);
    document.getElementById(`blv-${tabId}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const refreshAll = async () => {
    await fetchData();
    toast.success("Block data refreshed");
  };

  const handleExport = () => exportSchoolsCsv(filteredSchools);

  const districtLabel = block?.district_name
    ? block.district_name.charAt(0) + block.district_name.slice(1).toLowerCase()
    : "Pune";

  if (loading) {
    return (
      <div className="block-level-page">
        <div className="blv-loading-wrap">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  if (!block) {
    return (
      <div className="block-level-page">
        <div className="blv-not-found">
          <p>Block not found</p>
          <button type="button" className="blv-primary-action" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const pillClass = ragPillClass(block.rag_status);
  const ringClass = pillClass === "good" ? "good" : pillClass === "amber" ? "amber" : "critical";
  const criticalCount = metrics.criticalSchools || schools.filter((s) => s.rag_status === "red").length;
  const blockScopeLabel = `${districtLabel} · ${block.block_name} Block`;

  const openKpiModal = (metric) => setKpiModalMetric(metric);
  const handleKpiCardKeyDown = (event, metric) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openKpiModal(metric);
    }
  };

  return (
    <div className="block-level-page" data-testid="block-detail">
      <section className="blv-page-header" id="blv-overview">
        <div>
          <div className="blv-eyebrow">
            <svg className="blv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
            Block intelligence · {districtLabel} District · 2025–26
          </div>
          <div className="blv-back-row">
            <button
              type="button"
              className="blv-back-link"
              aria-label="Back"
              onClick={() => navigate(`/district/${block.district_code}`)}
            >
              ←
            </button>
            <h1>{block.block_name}</h1>
            <span className={`blv-status-pill ${pillClass}`}>{ragLabel(block.rag_status)}</span>
          </div>
          <p>
            Block-level command view for school coverage, student identity, infrastructure, risk signals and schools
            requiring immediate action.
          </p>
        </div>
        <div className="blv-header-actions">
          <button type="button" className="blv-secondary-action" onClick={handleExport}>
            <svg className="blv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M7 10l5 5 5-5" />
              <path d="M12 15V3" />
            </svg>
            Export
          </button>
          <button type="button" className="blv-primary-action" onClick={refreshAll}>
            <svg className="blv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <path d="M21 3v6h-6" />
            </svg>
            Refresh
          </button>
        </div>
      </section>

      <div className="blv-tabs-wrap" aria-label="Block sections">
        <nav className="blv-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`blv-tab${activeTab === tab.id ? " active" : ""}`}
              onClick={() => scrollToSection(tab.id)}
            >
              <TabIcon type={tab.icon} />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <section className="blv-block-hero">
        <div className="blv-block-hero-layout">
          <div className="blv-block-summary">
            <div>
              <div className="blv-block-title-mini">
                {districtLabel} · Block Code {block.block_code}
              </div>
              <div className="blv-summary-card" aria-label="Block score summary">
                <div className={`blv-score-ring ${ringClass}`} style={scoreRingStyle(block.shi_score ?? 0, ringClass)}>
                  <div>
                    <strong>{(block.shi_score ?? 0).toFixed(1)}</strong>
                    <span>OSH Score</span>
                  </div>
                </div>
                <div className="blv-summary-copy">
                  <h2>{narrative?.title}</h2>
                  <p>{narrative?.body}</p>
                  <div className="blv-metadata-row">
                    <span className="blv-metadata-chip">
                      📍 <b>{districtLabel}</b>
                    </span>
                    <span className="blv-metadata-chip">
                      🏫 <b>{fmt(block.total_schools)} schools</b>
                    </span>
                    <span className="blv-metadata-chip">
                      🧑‍🎓 <b>{formatLakh(block.total_students)} students</b>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="blv-kpi-grid" aria-label="Block KPIs">
            <article
              className="blv-kpi-card clickable"
              role="button"
              tabIndex={0}
              aria-label="Total Schools — open drill-down"
              onClick={() => openKpiModal("total_schools")}
              onKeyDown={(e) => handleKpiCardKeyDown(e, "total_schools")}
            >
              <div className="blv-kpi-top">
                <small>Total Schools</small>
                <span className="blv-kpi-icon">
                  <svg className="blv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 21h18" />
                    <path d="M5 21V7l8-4v18" />
                    <path d="M19 21V11l-6-4" />
                  </svg>
                </span>
              </div>
              <strong>{fmt(block.total_schools)}</strong>
              <span className="blv-kpi-hint">{fmt(metrics.activeSchools)} active in school scope</span>
            </article>
            <article
              className="blv-kpi-card green clickable"
              role="button"
              tabIndex={0}
              aria-label="Total Students — open drill-down"
              onClick={() => openKpiModal("total_students")}
              onKeyDown={(e) => handleKpiCardKeyDown(e, "total_students")}
            >
              <div className="blv-kpi-top">
                <small>Total Students</small>
                <span className="blv-kpi-icon">
                  <svg className="blv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-8 0v2" />
                    <circle cx="12" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  </svg>
                </span>
              </div>
              <strong>{fmt(block.total_students)}</strong>
              <span className="blv-kpi-hint">Large enrolment base</span>
            </article>
            <article
              className="blv-kpi-card purple clickable"
              role="button"
              tabIndex={0}
              aria-label="Aadhaar Compliance — open drill-down"
              onClick={() => openKpiModal("aadhaar_compliance")}
              onKeyDown={(e) => handleKpiCardKeyDown(e, "aadhaar_compliance")}
            >
              <div className="blv-kpi-top">
                <small>Aadhaar Compliance</small>
                <span className="blv-kpi-icon">
                  <svg className="blv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                </span>
              </div>
              <strong>{(block.aadhaar_percentage ?? 0).toFixed(1)}%</strong>
              <span className="blv-kpi-hint">Strong identity coverage</span>
            </article>
            <article
              className="blv-kpi-card orange warning clickable"
              role="button"
              tabIndex={0}
              aria-label="Critical Schools — open drill-down"
              onClick={() => openKpiModal("critical_schools")}
              onKeyDown={(e) => handleKpiCardKeyDown(e, "critical_schools")}
            >
              <div className="blv-kpi-top">
                <small>Critical Schools</small>
                <span className="blv-kpi-icon">
                  <svg className="blv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                    <path d="M12 9v4" />
                    <path d="M12 17h.01" />
                  </svg>
                </span>
              </div>
              <strong>{fmt(criticalCount)}</strong>
              <span className="blv-kpi-hint">Need weekly follow-up</span>
            </article>
          </div>
        </div>
      </section>

      <section className="blv-insights-card" id="blv-insights">
        <div className="blv-panel-head">
          <div className="blv-panel-title">
            <span className="blv-icon-box purple">
              <svg className="blv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v20" />
                <path d="M2 12h20" />
                <path d="M5 5l14 14" />
                <path d="M19 5 5 19" />
              </svg>
            </span>
            <div>
              <h3>AI Insights for {block.block_name} Block</h3>
              <p>Converted from a long text panel into short, scannable insight cards with root-cause signals and action prompts.</p>
            </div>
          </div>
          <span className="blv-pill">Block level · School scope</span>
        </div>
        <div className="blv-insight-grid">
          {insightCards.map((card) => (
            <article className="blv-insight-item" key={card.label}>
              <small>{card.label}</small>
              <strong>{card.title}</strong>
              <p>{card.body}</p>
            </article>
          ))}
        </div>
        <div className="blv-signals-row" id="blv-actions">
          {signalCards.map((signal) => (
            <article className="blv-signal-card" key={signal.title}>
              <span className={`blv-icon-box ${signal.variant}`}>
                {signal.variant === "red" ? (
                  <svg className="blv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                    <path d="M12 9v4" />
                    <path d="M12 17h.01" />
                  </svg>
                ) : signal.variant === "orange" ? (
                  <svg className="blv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 3v18h18" />
                    <path d="m19 9-5 5-4-4-3 3" />
                  </svg>
                ) : (
                  <svg className="blv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 11l2 2 4-4" />
                    <path d="M5 4h14v16H5z" />
                    <path d="M9 2h6v4H9z" />
                  </svg>
                )}
              </span>
              <div>
                <h4>{signal.title}</h4>
                <p>{signal.body}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <div className="blv-section-title" id="blv-diagnostics">
        <div>
          <h2>Block Diagnostics and Action Queue</h2>
          <p>Scores are separated from narrative insights so district officers can scan performance first and then act on clearly ranked actions.</p>
        </div>
        <button type="button" className="blv-section-action" onClick={() => scrollToSection("schools")}>
          View schools →
        </button>
      </div>

      <section className="blv-diagnostics-grid">
        <article className="blv-panel">
          <div className="blv-panel-head">
            <div>
              <h3>Performance Matrix</h3>
              <p>Key block indicators ranked by current operating strength.</p>
            </div>
            <span className="blv-pill">Latest snapshot</span>
          </div>
          <div className="blv-scorecard-list">
            {performanceRows.map((row) => {
              const barWidth = row.barScale ? Math.min(row.value * 10, 100) : Math.min(row.value, 100);
              const display =
                row.label === "OSH Score"
                  ? row.value.toFixed(1)
                  : `${row.value.toFixed(row.label === "Dropout Risk" ? 2 : 1)}%`;
              return (
                <div className="blv-score-row" key={row.label}>
                  <div className="blv-score-label">{row.label}</div>
                  <div className="blv-score-track">
                    <div className={`blv-score-fill ${row.fill}`} style={{ width: `${barWidth}%` }} />
                  </div>
                  <div className="blv-score-value">{display}</div>
                </div>
              );
            })}
          </div>
        </article>

        <article className="blv-panel">
          <div className="blv-panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Ranked by urgency and expected impact on block health.</p>
            </div>
            <span className="blv-pill">{actionQueue.length} actions</span>
          </div>
          <div className="blv-action-queue">
            {actionQueue.map((item, idx) => (
              <div className="blv-queue-item" key={item.title}>
                <div className="blv-queue-num">{idx + 1}</div>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.body}</p>
                </div>
                <span className={`blv-priority-badge ${item.priorityClass}`}>{item.priority}</span>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="blv-panel blv-table-panel" id="blv-schools">
        <div className="blv-table-head">
          <div>
            <h3>Schools Requiring Attention</h3>
            <p>
              {fmt(filteredSchools.length)} schools shown with improved filters, sticky table header, status badges and risk
              indicators.
            </p>
          </div>
          <span className="blv-pill">Block {block.block_code}</span>
        </div>
        <div className="blv-table-toolbar">
          <div className="blv-left-tools">
            <label className="blv-table-search">
              <svg className="blv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                placeholder="Search school name or UDISE..."
                aria-label="Search schools"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </label>
            {[
              { id: "all", label: "All Schools" },
              { id: "critical", label: "Critical" },
              { id: "zero-teacher", label: "Zero Teacher" },
              { id: "low-aadhaar", label: "Low Aadhaar" },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                className={`blv-filter-pill${schoolFilter === f.id ? " active" : ""}`}
                onClick={() => setSchoolFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="blv-right-tools">
            {categories.length > 2 && (
              <select
                className="blv-category-select"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                aria-label="Category filter"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat === "all" ? "Category" : cat}
                  </option>
                ))}
              </select>
            )}
            <button type="button" className="blv-filter-pill" onClick={handleExport}>
              Export CSV
            </button>
          </div>
        </div>
        <div className="blv-table-wrap">
          <table aria-label="Schools table">
            <thead>
              <tr>
                <th>School Name</th>
                <th>Category</th>
                <th>Students</th>
                <th>Teachers</th>
                <th>PTR</th>
                <th>Aadhaar %</th>
                <th>OSH Score</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row) => {
                const status = schoolStatusMeta(row.rag_status);
                const shi = row.shi_score ?? 0;
                const barClass = shi >= 70 ? "green" : "";
                return (
                  <tr key={row.udise_code} onClick={() => navigate(`/school/${row.udise_code}`)}>
                    <td className="blv-school-name">{row.school_name}</td>
                    <td>{row.school_category || row.school_management || "—"}</td>
                    <td>{fmt(row.total_students)}</td>
                    <td>{fmt(row.total_teachers)}</td>
                    <td>{(row.ptr ?? 0).toFixed(1)}</td>
                    <td>{(row.aadhaar_percentage ?? 0).toFixed(1)}%</td>
                    <td>
                      <span className="blv-mini-bar">
                        <span className="blv-mini-track">
                          <i className={`blv-mini-fill ${barClass}`} style={{ width: `${Math.min(shi, 100)}%` }} />
                        </span>
                        {shi.toFixed(1)}%
                      </span>
                    </td>
                    <td>
                      <span className={`blv-badge ${status.className}`}>{status.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="blv-table-foot">
          <span>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredSchools.length)} of{" "}
            {fmt(filteredSchools.length)} schools · Sorted by criticality
          </span>
          <div className="blv-pager">
            <button type="button" className="blv-page-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              ‹
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum = i + 1;
              if (totalPages > 5) {
                if (page <= 3) pageNum = i + 1;
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = page - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  type="button"
                  className={`blv-page-btn${page === pageNum ? " active" : ""}`}
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            <button type="button" className="blv-page-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              ›
            </button>
          </div>
        </div>
      </section>

      <BlockKpiSchoolsModal
        open={Boolean(kpiModalMetric)}
        metric={kpiModalMetric}
        onClose={() => setKpiModalMetric(null)}
        schools={schools}
        block={block}
        scopeLabel={blockScopeLabel}
      />
    </div>
  );
};

export default BlockDetail;
